/**
 * Video URL handling for testimonials.
 *
 * Supports four sources, because that is what people actually have:
 *   · Firebase Storage uploads  → plays natively in <video>
 *   · YouTube / Vimeo links     → iframe embed
 *   · Google Drive links        → iframe embed (Drive blocks direct <video> use)
 *   · Any direct .mp4/.webm URL → plays natively
 *
 * Lives here rather than in the admin panel so the public site doesn't have to
 * import from an admin module to render a testimonial.
 */

/** Pull the file id out of any of Drive's several link shapes. */
export function parseDriveFileId(url = '') {
  const patterns = [
    /drive\.google\.com\/file\/d\/([\w-]{10,})/,      // /file/d/<id>/view
    /docs\.google\.com\/file\/d\/([\w-]{10,})/,       // legacy docs host
    /drive\.google\.com\/open\?id=([\w-]{10,})/,      // /open?id=<id>
    /drive\.google\.com\/uc\?(?:export=\w+&)?id=([\w-]{10,})/, // /uc?id=<id>
    /[?&]id=([\w-]{25,})/,                            // bare ?id= fallback
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

/** Pull the folder id out of a Drive folder link. */
export function parseDriveFolderId(url = '') {
  const match = url.match(/drive\.google\.com\/drive\/(?:u\/\d+\/)?folders\/([\w-]{10,})/);
  return match ? match[1] : null;
}

export const isDriveUrl = (url = '') => Boolean(parseDriveFileId(url) || parseDriveFolderId(url));

/** Drive's inline player. The file must be shared "anyone with the link". */
export const driveEmbedUrl = (fileId) => `https://drive.google.com/file/d/${fileId}/preview`;

/** Direct download/stream URL for public Google Drive video files. */
export const driveDirectStreamUrl = (fileId) => `https://docs.google.com/uc?export=download&id=${fileId}`;

/** Drive-generated poster frame. Cheap, and avoids loading the video to preview it. */
export const driveThumbnailUrl = (fileId, width = 640) =>
  `https://drive.google.com/thumbnail?id=${fileId}&sz=w${width}`;

/**
 * Convert a share URL into something embeddable.
 * Returns null when the URL is a direct video file, which plays natively.
 */
export function toEmbedUrl(url = '') {
  const youtube = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([\w-]{6,})/
  );
  if (youtube) {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const originParam = origin ? `?enablejsapi=1&origin=${encodeURIComponent(origin)}` : '?enablejsapi=1';
    return `https://www.youtube.com/embed/${youtube[1]}${originParam}`;
  }

  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;

  const driveId = parseDriveFileId(url);
  if (driveId) return driveEmbedUrl(driveId);

  return null;
}

/** Best-effort poster frame for a share URL, when the host exposes one. */
export function toPosterUrl(url = '') {
  const driveId = parseDriveFileId(url);
  // 640 and no wider. Drive only generates the sizes it holds for a file, and
  // for video it tops out here — asking for w1000 returned nothing at all for
  // every testimonial, so every poster failed and the cards rendered blank.
  if (driveId) return driveThumbnailUrl(driveId, 640);

  const youtube = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/
  );
  if (youtube) return `https://img.youtube.com/vi/${youtube[1]}/hqdefault.jpg`;

  return '';
}

/**
 * Is a Drive file playable by someone who is not signed in?
 *
 * Drive answers its thumbnail endpoint with the image for a link-shared file
 * and with an HTML sign-in page for a private one — which the browser cannot
 * decode as an image. So a plain <img> load is a reliable public/private
 * probe, needs no API key, and settles in well under a second.
 *
 * A private file is the single most common reason a testimonial "does not
 * play": the visitor gets Google's "Can't access your Google Account" panel
 * inside the player.
 *
 * @returns {Promise<'public'|'private'|'unknown'>}
 */
export async function probeDrivePublic(fileId, timeoutMs = 8000) {
  if (!fileId || typeof Image === 'undefined') return 'unknown';
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'unknown';

  const attempt = () =>
    new Promise((resolve) => {
      const image = new Image();
      let settled = false;

      const finish = (verdict) => {
        if (settled) return;
        settled = true;
        image.onload = null;
        image.onerror = null;
        resolve(verdict);
      };

      image.onload = () => finish('public');
      image.onerror = () => finish('unknown');
      setTimeout(() => finish('unknown'), timeoutMs);

      // Cache-busted, so re-checking after a fix in Drive sees the new answer.
      image.src = `${driveThumbnailUrl(fileId, 200)}&probe=${Date.now()}`;
    });

  const first = await attempt();
  if (first !== 'private') return first;

  // A verdict of 'private' hides the video from the public site, so confirm it
  // once. A dropped connection or a momentary 429 from Google should not take
  // a working testimonial off the page.
  await new Promise((resolve) => setTimeout(resolve, 400));
  return attempt();
}

/** Human label for where a video is hosted. */
export function describeSource(url = '') {
  if (/firebasestorage\.googleapis\.com/.test(url) || /testimonial-videos/.test(url)) return 'Firebase Storage';
  if (/youtu\.?be/.test(url)) return 'YouTube';
  if (/vimeo\.com/.test(url)) return 'Vimeo';
  if (parseDriveFileId(url)) return 'Google Drive';
  return 'Uploaded';
}

/**
 * List video files in a public Drive folder.
 *
 * Needs a Google API key with the Drive API enabled, and the folder shared
 * "anyone with the link". Without a key this throws with an explanation rather
 * than failing silently.
 */
export async function listDriveFolderVideos(folderUrl, apiKey) {
  const folderId = parseDriveFolderId(folderUrl);
  if (!folderId) throw new Error('That does not look like a Google Drive folder link.');

  if (!apiKey) {
    throw new Error(
      'Folder import needs a Google API key. Add VITE_GOOGLE_API_KEY to .env, or paste the individual file links instead.'
    );
  }

  const query = encodeURIComponent(`'${folderId}' in parents and mimeType contains 'video/'`);
  const fields = encodeURIComponent('files(id,name,mimeType,thumbnailLink,videoMediaMetadata)');
  const endpoint =
    `https://www.googleapis.com/drive/v3/files?q=${query}&key=${apiKey}` +
    `&fields=${fields}&pageSize=100&supportsAllDrives=true&includeItemsFromAllDrives=true`;

  const response = await fetch(endpoint);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const reason = data?.error?.message || `HTTP ${response.status}`;
    if (response.status === 403 || response.status === 404) {
      throw new Error(
        `Drive refused the request (${reason}). Check the folder is shared "Anyone with the link" and the API key allows the Drive API.`
      );
    }
    throw new Error(`Could not read that folder (${reason}).`);
  }

  const files = data.files || [];
  if (files.length === 0) {
    throw new Error('No video files found in that folder. Is it shared publicly?');
  }

  return files.map((file) => ({
    fileId: file.id,
    name: file.name,
    videoUrl: `https://drive.google.com/file/d/${file.id}/view`,
    posterUrl: file.thumbnailLink || driveThumbnailUrl(file.id),
  }));
}

/** Split a pasted blob of text into the individual URLs it contains. */
export function extractUrls(text = '') {
  const matches = text.match(/https?:\/\/[^\s,"'<>]+/g) || [];
  return [...new Set(matches)];
}
